import React, { useEffect, useState } from "react";
import apiDefinitions from "../../../core/api/apiDefinitions";
import { showError } from "../../../core/alert/sweetAlertConfig";

interface VisitorRequestCounts {
  totalRequests: number;
  pendingApproval: number;
  approvedRequests: number;
  rejectedRequests: number;
  finalApprovedRequests: number;
  finalRejectedRequests: number;
  visitorsInside: number;
  date?: string;
}

const VisitorRequest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VisitorRequestCounts | null>(null);

  const fetchVisitorRequests = async () => {
    setLoading(true);
    try {
      const response = await apiDefinitions.getVisitorRequestCounts();
      if (response?.data?.status === "S1000") {
        setData(response.data.data);
      } else {
        showError(
          "Failed to fetch visitor request counts: " +
            (response?.data?.statusDescription || "Unknown error")
        );
      }
    } catch (error: any) {
      showError(
        "Failed to fetch visitor request counts: " +
          (error.response?.data?.statusDescription || "Network error")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitorRequests();
  }, []);

  return (
    <div className="rm-glass-card rm-glass-card-interactive flex-fill w-100 p-3 p-md-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3 mb-3 border-bottom border-light">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "42px",
              height: "42px",
              background: "var(--rm-secondary-light)",
              color: "var(--rm-secondary)",
              fontSize: "1.25rem",
            }}
          >
            <i className="ti ti-users" />
          </div>
          <div>
            <h5 className="m-0 fw-bold" style={{ color: "var(--rm-text-primary)" }}>
              Visitor Requests
            </h5>
            <small style={{ color: "var(--rm-text-muted)" }}>
              {loading
                ? "Refreshing..."
                : `Total: ${data?.totalRequests ?? 0} ${
                    data?.date ? `• as of ${new Date(data.date).toLocaleDateString()}` : ""
                  }`}
            </small>
          </div>
        </div>

        <button
          onClick={fetchVisitorRequests}
          disabled={loading}
          className="btn btn-sm btn-light rounded-pill px-3 shadow-sm border-0 d-inline-flex align-items-center gap-1"
          style={{ fontSize: "0.8rem", color: "var(--rm-text-secondary)" }}
          title="Refresh Data"
        >
          <i className={`ti ti-refresh ${loading ? "rotate" : ""}`} />
          <span className="d-none d-sm-inline">Refresh</span>
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
          <p className="small mb-0 text-muted">Updating visitor counts...</p>
        </div>
      ) : (
        <div className="row g-2 g-md-3">
          {/* Pending Approval */}
          <div className="col-6 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-warning)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-warning)" }}
              >
                {data?.pendingApproval ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.75rem", fontWeight: 600 }}>
                Pending
              </small>
            </div>
          </div>

          {/* Approved */}
          <div className="col-6 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-info)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-info)" }}
              >
                {data?.approvedRequests ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.75rem", fontWeight: 600 }}>
                Approved
              </small>
            </div>
          </div>

          {/* Final Approved */}
          <div className="col-6 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-success)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-success)" }}
              >
                {data?.finalApprovedRequests ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.75rem", fontWeight: 600 }}>
                Final Approved
              </small>
            </div>
          </div>

          {/* Rejected */}
          <div className="col-6 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-danger)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-danger)" }}
              >
                {data?.rejectedRequests ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.75rem", fontWeight: 600 }}>
                Rejected
              </small>
            </div>
          </div>

          {/* Final Rejected */}
          <div className="col-6 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-text-muted)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-text-secondary)" }}
              >
                {data?.finalRejectedRequests ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-text-secondary)", fontSize: "0.75rem", fontWeight: 600 }}>
                Final Rejected
              </small>
            </div>
          </div>

          {/* Visitors Inside */}
          <div className="col-6 col-sm-4">
            <div
              className="rm-sub-glass-item text-center p-3"
              style={{ borderLeft: "3px solid var(--rm-primary)", background: "rgba(79, 70, 229, 0.05)" }}
            >
              <div
                className="rm-sub-glass-num fw-bold mb-1"
                style={{ color: "var(--rm-primary)" }}
              >
                {data?.visitorsInside ?? 0}
              </div>
              <small className="d-block text-truncate" style={{ color: "var(--rm-primary)", fontSize: "0.75rem", fontWeight: 700 }}>
                Inside Campus
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorRequest;
